import React from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Layers, ExternalLink, ShoppingCart } from "lucide-react";

export default function PortalProducts() {
  const [data, setData] = React.useState({ live: [], available: [] });
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState("live");

  React.useEffect(() => {
    (async () => {
      const r = await api.get("/portal/me/stax");
      setData(r.data);
      setLoading(false);
    })();
  }, []);

  const list = tab === "live" ? data.live : data.available;

  return (
    <div className="space-y-6" data-testid="portal-products-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Stax catalog</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Your products</h1>
        <p className="text-slate-500 text-sm mt-1">Live products you're using today and the rest of the Xentra Stax catalog available to you.</p>
      </div>

      <div className="flex gap-1" data-testid="portal-products-tabs">
        <TabBtn active={tab === "live"} onClick={() => setTab("live")} testid="portal-products-tab-live">
          Live <span className="ml-2 text-[10px] font-mono opacity-70">{data.live.length}</span>
        </TabBtn>
        <TabBtn active={tab === "available"} onClick={() => setTab("available")} testid="portal-products-tab-available">
          Available <span className="ml-2 text-[10px] font-mono opacity-70">{data.available.length}</span>
        </TabBtn>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono text-sm">Loading…</div>
      ) : list.length === 0 ? (
        <Card className="border-slate-200 p-12 text-center">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-heading text-lg text-slate-700">
            {tab === "live" ? "No live products yet" : "Nothing in the catalog right now"}
          </div>
          <div className="text-sm text-slate-500 mt-1">
            {tab === "live" ? "When products go live for you, they'll show up here." : "Speak to your account manager about what's coming."}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((p) => (
            <Card key={p.id} className="border-slate-200" data-testid={`portal-product-${p.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-heading text-base font-bold text-slate-900 truncate">{p.name}</div>
                      {tab === "live" ? (
                        <span className="px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider rounded border bg-emerald-50 text-emerald-700 border-emerald-100">Live</span>
                      ) : (
                        <span className="px-2 py-0.5 text-[10px] uppercase font-mono tracking-wider rounded border bg-slate-100 text-slate-600 border-slate-200">Available</span>
                      )}
                    </div>
                    <div className="text-xs font-mono text-slate-500 mt-0.5">{p.category}</div>
                  </div>
                  {p.url && (
                    <a href={p.url} target="_blank" rel="noreferrer"
                      className="text-blue-600 hover:text-blue-800" title="Visit product site">
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
                {p.description && (
                  <div className="text-sm text-slate-600 mt-3 leading-relaxed">{p.description}</div>
                )}
                {tab === "live" && p.first_used_at && (
                  <div className="mt-3 text-xs text-slate-500 font-mono">
                    Live since {fmtDate(p.first_used_at)}
                  </div>
                )}
                {tab === "available" && (
                  <div className="mt-3 text-xs text-blue-700 flex items-center gap-1">
                    <ShoppingCart className="h-3.5 w-3.5" />
                    Ask your account manager to enable
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 text-sm font-medium rounded-md border transition-colors ${
        active ? "bg-blue-600 text-white border-blue-600"
          : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-700"
      }`}
    >
      {children}
    </button>
  );
}
